import { DOCUMENT, Service, computed, inject, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

/**
 * Cuánto después de una acción del usuario (clic, tecla, navegación) un
 * request todavía cuenta como "lo que pidió", en milisegundos.
 */
const VENTANA_ACCION_MS = 2_000;

/**
 * Contador de operaciones en vuelo, para la barra de progreso del encabezado.
 *
 * Es un contador y no un booleano porque hay varias peticiones simultáneas: la
 * barra se apaga cuando termina la última, no cuando termina la primera.
 *
 * **Sólo cuenta lo que pidió el usuario.** Los refrescos automáticos (listados
 * y mapa cada minuto, el aviso de eventos cada 10 s, Descargas…) no la
 * encienden: la pantalla conserva lo que muestra mientras llega lo nuevo, y una
 * barra que aparece sola cada pocos segundos era un destello sin información.
 * Se distingue por tiempo: un request cuenta si arranca poco después de un
 * clic, una tecla o una navegación. Un sondeo no viene de ninguno de esos.
 */
@Service()
export class LoadingService {
  private readonly enVuelo = signal(0);
  /** Arranca "recién": la carga inicial de la aplicación también cuenta. */
  private ultimaAccion = performance.now();

  readonly cargando = computed(() => this.enVuelo() > 0);

  constructor() {
    const marcar = () => (this.ultimaAccion = performance.now());
    const doc = inject(DOCUMENT);
    doc.addEventListener('pointerdown', marcar, { capture: true, passive: true });
    doc.addEventListener('keydown', marcar, { capture: true, passive: true });
    inject(Router).events.subscribe((e) => {
      if (e instanceof NavigationStart || e instanceof NavigationEnd) marcar();
    });
  }

  /** Si un request que arranca ahora lo pidió el usuario (ver la clase). */
  pedidoPorElUsuario(): boolean {
    return performance.now() - this.ultimaAccion <= VENTANA_ACCION_MS;
  }

  arrancar(): void {
    this.enVuelo.update((n) => n + 1);
  }

  terminar(): void {
    this.enVuelo.update((n) => Math.max(0, n - 1));
  }
}
